import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import useStyles from "./styles";
import { AuditDetailDrawer } from "./AuditDetailDrawer";
import {
  AuditFilters,
  actionLabel,
  downloadAuditCsv,
  severityColor,
  useAuditFilterOptions,
  useAuditLogs,
} from "../../api/audit";
import { useAuth } from "../../auth/useAuth";

/**
 * The audit trail table — who did what.
 *
 * Filters live in local state and are passed straight into the query key, so
 * every filter change is a new cached page rather than a refetch of the same
 * one. `placeholderData` in the hook keeps the previous rows on screen while
 * the new page loads, which stops the table flashing empty on every keystroke.
 */

interface Props {
  /** Prefilled filter, used when the tab is opened from a KPI card. */
  initialFilters?: AuditFilters;
}

export const AuditTrailTab: React.FC<Props> = ({ initialFilters }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { can } = useAuth();

  const [filters, setFilters] = useState<AuditFilters>({
    page: 1,
    per_page: 25,
    ...initialFilters,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { logs, pagination, isLoading } = useAuditLogs(filters);
  const { options } = useAuditFilterOptions();

  /** Any filter change resets to page 1 — page 7 of a new filter is nonsense. */
  const setFilter = (key: keyof AuditFilters, value: any) =>
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));

  const reset = () => setFilters({ page: 1, per_page: filters.per_page });

  const hasActiveFilters = Object.entries(filters).some(
    ([k, v]) => !["page", "per_page"].includes(k) && v
  );

  return (
    <>
      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div className={classes.filterBar}>
        <TextField
          size="small"
          label={t("audit.search", "Rechercher")}
          placeholder={t("audit.searchPlaceholder", "Email, IP, enregistrement…")}
          value={filters.q ?? ""}
          onChange={(e) => setFilter("q", e.target.value)}
          sx={{ minWidth: 240 }}
        />

        <TextField
          select
          size="small"
          label={t("audit.category", "Catégorie")}
          value={filters.category ?? ""}
          onChange={(e) => setFilter("category", e.target.value)}
        >
          <MenuItem value="">{t("audit.all", "Toutes")}</MenuItem>
          {Object.entries(options?.categories ?? {}).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("audit.severity", "Gravité")}
          value={filters.severity ?? ""}
          onChange={(e) => setFilter("severity", e.target.value)}
        >
          <MenuItem value="">{t("audit.all", "Toutes")}</MenuItem>
          {(options?.severities ?? []).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("audit.action", "Action")}
          value={filters.action ?? ""}
          onChange={(e) => setFilter("action", e.target.value)}
        >
          <MenuItem value="">{t("audit.all", "Toutes")}</MenuItem>
          {(options?.actions ?? []).map((a) => (
            <MenuItem key={a} value={a}>
              {actionLabel(a)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("audit.type", "Type")}
          value={filters.auditable_type ?? ""}
          onChange={(e) => setFilter("auditable_type", e.target.value)}
        >
          <MenuItem value="">{t("audit.all", "Tous")}</MenuItem>
          {(options?.types ?? []).map((ty) => (
            <MenuItem key={ty.value} value={ty.value}>
              {ty.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("audit.actor", "Auteur")}
          value={filters.actor_id ?? ""}
          onChange={(e) => setFilter("actor_id", e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t("audit.all", "Tous")}</MenuItem>
          {(options?.actors ?? []).map((a) => (
            <MenuItem key={a.actor_id} value={a.actor_id}>
              {a.actor_name || a.actor_email || `#${a.actor_id}`}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          type="date"
          label={t("audit.from", "Du")}
          InputLabelProps={{ shrink: true }}
          value={filters.date_from ?? ""}
          onChange={(e) => setFilter("date_from", e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label={t("audit.to", "Au")}
          InputLabelProps={{ shrink: true }}
          value={filters.date_to ?? ""}
          onChange={(e) => setFilter("date_to", e.target.value)}
        />

        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={reset}
            color="inherit"
          >
            {t("audit.reset", "Réinitialiser")}
          </Button>
        )}

        <span className={classes.filterSpacer} />

        {/*
          Export is gated on its own permission. Hiding the button for someone
          who lacks it is honesty, not security — the endpoint enforces the
          same key server-side, and the export writes its own audit entry.
        */}
        {can("platform.audit.export") && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() =>
              downloadAuditCsv(
                filters,
                t("audit.exportError", "Échec de l'export")
              )
            }
          >
            {t("audit.export", "Exporter CSV")}
          </Button>
        )}
      </div>

      {/* ── Table ───────────────────────────────────────────────────── */}
      <TableContainer component={Paper} className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("audit.date", "Date")}</TableCell>
              <TableCell>{t("audit.action", "Action")}</TableCell>
              <TableCell>{t("audit.type", "Type")}</TableCell>
              <TableCell>{t("audit.record", "Enregistrement")}</TableCell>
              <TableCell>{t("audit.actor", "Auteur")}</TableCell>
              <TableCell>{t("audit.changes", "Modifications")}</TableCell>
              <TableCell>{t("audit.severity", "Gravité")}</TableCell>
              <TableCell>{t("audit.ip", "IP")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box className={classes.emptyState}>
                    {hasActiveFilters
                      ? t(
                          "audit.emptyFiltered",
                          "Aucune entrée ne correspond à ces filtres."
                        )
                      : t("audit.empty", "Aucune entrée pour le moment.")}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {logs.map((log) => (
              <TableRow
                key={log.id}
                onClick={() => setSelectedId(log.id)}
                className={`${classes.clickableRow} ${
                  log.severity === "critical" ? classes.criticalRow : ""
                }`}
              >
                <TableCell className={classes.monoSmall} sx={{ whiteSpace: "nowrap" }}>
                  {log.created_at
                    ? new Date(log.created_at).toLocaleString("fr-FR")
                    : "—"}
                </TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {actionLabel(log.action)}
                </TableCell>

                <TableCell>{log.subject_label ?? "—"}</TableCell>

                <TableCell sx={{ maxWidth: 200 }}>
                  <div className={classes.actorName}>
                    {log.auditable_label ??
                      (log.auditable_id ? `#${log.auditable_id}` : "—")}
                  </div>
                </TableCell>

                <TableCell sx={{ maxWidth: 200 }}>
                  <div className={classes.actorCell}>
                    <span className={classes.actorName}>
                      {log.actor_name ||
                        log.actor_email ||
                        (log.actor_type === "system"
                          ? t("audit.systemActor", "Système")
                          : t("audit.anonymous", "Anonyme"))}
                    </span>
                    {log.actor_email && log.actor_name && (
                      <span className={classes.actorEmail}>
                        {log.actor_email}
                      </span>
                    )}
                  </div>
                </TableCell>

                <TableCell sx={{ maxWidth: 220 }}>
                  {/*
                    Field names, not a count. "3 champs" tells an operator
                    nothing; "monthly_price, is_active" tells them whether the
                    row is worth opening.
                  */}
                  <span className={classes.monoSmall}>
                    {log.changed_fields?.length
                      ? log.changed_fields.slice(0, 3).join(", ") +
                        (log.changed_fields.length > 3
                          ? ` +${log.changed_fields.length - 3}`
                          : "")
                      : "—"}
                  </span>
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    label={log.severity}
                    color={severityColor(log.severity)}
                  />
                </TableCell>

                <TableCell className={classes.monoSmall}>
                  {log.ip_address ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination?.total ?? 0}
        page={Math.max(0, (pagination?.current_page ?? 1) - 1)}
        onPageChange={(_e, page) =>
          setFilters((prev) => ({ ...prev, page: page + 1 }))
        }
        rowsPerPage={pagination?.per_page ?? 25}
        onRowsPerPageChange={(e) =>
          setFilters((prev) => ({
            ...prev,
            per_page: parseInt(e.target.value, 10),
            page: 1,
          }))
        }
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage={t("audit.rowsPerPage", "Lignes")}
      />

      <AuditDetailDrawer
        logId={selectedId}
        onClose={() => setSelectedId(null)}
        onNavigate={(id) => setSelectedId(id)}
      />
    </>
  );
};
